import { MicIcon, SquareIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Spinner } from '@/components/ui/spinner'
import { kindLabel } from '@/lib/format'
import { useRunTool, useSettings } from '@/lib/items'
import { cn } from '@/lib/utils'
import { useVoiceAvailable } from '@/lib/voice/available'
import { type Dictation, dictationErrorText, useDictation } from '@/lib/voice/use-dictation'
import { CapturePreview, useCapturePreview } from './capture-preview'
import { useWorkspace } from './workspace'

/** The input level as a bar under the field while listening (speech moves it). */
function LevelMeter({ level }: { level: number }) {
  // Speech is mostly below 0.3 RMS: scale so normal speaking fills most of the bar.
  const width = Math.min(1, level * 4)
  return (
    <div className="h-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <div
        className="h-full origin-left rounded-full bg-primary transition-transform duration-100"
        style={{ transform: `scaleX(${width})` }}
        data-testid="dictation-level"
      />
    </div>
  )
}

/**
 * What the dictation is doing, for the line under the field. While listening: how to stop, with
 * keys on a keyboard, with the button on a touch screen.
 */
function DictationStatus({ state }: { state: Dictation }) {
  if (state.phase !== 'starting' && state.phase !== 'listening' && state.phase !== 'finishing') {
    return null
  }
  return (
    <p
      className="text-sm text-muted-foreground"
      role="status"
      data-testid="dictation-status"
      data-phase={state.phase}
    >
      {state.phase === 'starting'
        ? 'Starting the microphone…'
        : state.phase === 'finishing'
          ? 'Getting the text…'
          : 'Listening…'}
      {state.phase === 'listening' ? (
        <>
          <span className="max-md:hidden"> Enter or the microphone stops, Esc cancels.</span>
          <span className="md:hidden"> Tap the square when you are done.</span>
        </>
      ) : null}
    </p>
  )
}

/**
 * Quick capture (C, or "New item"): free text in English or German, parsed on the server. Or
 * dictated (V, or the microphone): the words appear as they are understood, the final text lands
 * in the field, and Enter adds it like typed text (recorded as via voice).
 */
export function CaptureDialog() {
  const { captureOpen, setCaptureOpen, dictateOnOpen, clearDictateOnOpen } = useWorkspace()
  const { run } = useRunTool()
  const settings = useSettings()
  const timeZone = settings.data?.timezone ?? 'Europe/Zurich'
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  /** The text came from dictation (until the field is cleared). */
  const [dictated, setDictated] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dictation = useDictation((heard) => {
    setText(heard)
    setDictated(true)
    // Back in the field, ready for Enter (or a correction).
    requestAnimationFrame(() => inputRef.current?.focus())
  })
  const voice = useVoiceAvailable()
  const { state } = dictation
  const shown = state.phase === 'listening' || state.phase === 'finishing' ? state.partial : text
  const preview = useCapturePreview(dictation.active ? '' : text)

  useEffect(() => {
    if (captureOpen) return
    setText('')
    setDictated(false)
    dictation.cancel()
  }, [captureOpen, dictation.cancel])

  useEffect(() => {
    if (!captureOpen || !dictateOnOpen) return
    clearDictateOnOpen()
    if (voice) void dictation.start()
  }, [captureOpen, dictateOnOpen, clearDictateOnOpen, voice, dictation.start])

  const submit = async () => {
    if (state.phase === 'listening') {
      dictation.stop()
      return
    }
    if (dictation.active) return
    const trimmed = text.trim()
    if (!trimmed || saving) return
    setSaving(true)
    const result = await run(
      'item.capture',
      { text: trimmed },
      {
        done: (r) => {
          const who = r.item.counterpart ? ` · ${r.item.counterpart.name}` : ''
          return `Added to ${kindLabel(r.item.kind)}${who}`
        },
        via: dictated ? 'voice' : 'ui',
      },
    )
    setSaving(false)
    if (result) setCaptureOpen(false)
  }

  const error =
    state.phase === 'error' ? dictationErrorText(state.error, state.partial !== '') : null

  return (
    <Dialog
      open={captureOpen}
      onOpenChange={(open) => {
        // Esc while dictating cancels the dictation; a second Esc closes.
        if (!open && dictation.active) {
          dictation.cancel()
          return
        }
        setCaptureOpen(open)
      }}
    >
      <DialogContent className="top-[20%] translate-y-0 sm:max-w-lg" data-testid="capture-dialog">
        <DialogHeader>
          <DialogTitle>Capture</DialogTitle>
          <DialogDescription>
            {voice
              ? 'Type or say it, in English or German: “Send Anna the contract by Fri”.'
              : 'Type it like you would say it, in English or German: “Send Anna the contract by Fri”.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              autoFocus
              aria-label="What do you need to remember?"
              placeholder={
                state.phase === 'listening' || state.phase === 'starting'
                  ? 'Listening…'
                  : 'What do you need to remember?'
              }
              value={shown}
              readOnly={dictation.active}
              data-dictating={dictation.active || undefined}
              onKeyDown={(event) => {
                // The Add button is disabled while dictating, which blocks Enter from submitting:
                // Enter stops listening here instead.
                if (event.key !== 'Enter' || !dictation.active) return
                event.preventDefault()
                if (state.phase === 'listening') dictation.stop()
              }}
              onChange={(e) => {
                setText(e.target.value)
                if (!e.target.value) setDictated(false)
                if (state.phase === 'error') dictation.reset()
              }}
              maxLength={1000}
            />
            {voice ? (
              <Button
                type="button"
                variant={dictation.active ? 'default' : 'outline'}
                size="icon"
                className="shrink-0 max-sm:size-10"
                aria-label={dictation.active ? 'Stop dictating' : 'Dictate (V)'}
                aria-pressed={dictation.active}
                title={dictation.active ? 'Stop dictating' : 'Dictate (V)'}
                data-testid="dictate"
                disabled={state.phase === 'starting' || state.phase === 'finishing'}
                onClick={() => {
                  if (state.phase === 'listening') dictation.stop()
                  else if (!dictation.active) void dictation.start()
                }}
              >
                {state.phase === 'starting' || state.phase === 'finishing' ? (
                  <Spinner aria-hidden="true" />
                ) : state.phase === 'listening' ? (
                  <SquareIcon aria-hidden="true" />
                ) : (
                  <MicIcon aria-hidden="true" />
                )}
              </Button>
            ) : null}
          </div>
          {state.phase === 'listening' ? <LevelMeter level={state.level} /> : null}
          <DictationStatus state={state} />
          {error ? (
            <p className="text-sm text-destructive" role="alert" data-testid="dictation-error">
              {error}
            </p>
          ) : null}
          <div
            className={cn(
              'flex min-h-6 flex-wrap items-center gap-1.5 text-sm',
              dictation.active && 'opacity-50',
            )}
            data-testid="capture-preview"
            aria-live="polite"
          >
            {preview ? (
              <CapturePreview preview={preview} timeZone={timeZone} />
            ) : (
              <span className="text-muted-foreground">
                The preview shows the list, person and due date.
              </span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-xs text-muted-foreground">
              <Kbd>↵</Kbd> to add · <Kbd>Esc</Kbd> to cancel
            </span>
            <Button type="submit" disabled={!text.trim() || saving || dictation.active}>
              Add
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
